// src/components/kb/KbDocumentDetail.tsx
//
// Routed KB document detail (/kb/:documentId) — Stories 2.1 + 2.2. A metadata
// card with an explicit edit mode (title, brand, rep-shareable; Save/Cancel), a
// provenance meta line (source filename, added/updated, uploader when known), a
// read-only preview of the latest version's content_text (collapsed with "Show
// full content"), a "Replace content" action that creates a NEW immutable
// version through the kb-ingest pipeline, and the version history (newest-first,
// each version deep-linkable at /kb/:documentId/versions/:versionId). Editing
// metadata never creates a content version (content-immutability applies to
// content, not labels); a replacement is additive — prior versions stay
// readable and pinned.

import React, { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  Archive,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  History,
  Pencil,
  RefreshCw,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { useKbDocument } from '../../hooks/useKbDocuments'
import { KbReplaceForm } from './KbReplaceForm'
import { formatDateTime } from '../../utils/dateUtils'
import type { YakKbDocVersion } from '../../types/yak'

// Collapsed preview height before "Show full content" reveals the rest.
const PREVIEW_COLLAPSED_CHARS = 1200

export function KbDocumentDetail() {
  const { documentId } = useParams<{ documentId: string }>()
  const {
    document,
    latestVersion,
    versions,
    loading,
    error,
    updateMetadata,
    replaceContent,
    retire,
  } = useKbDocument(documentId ?? '')

  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState('')
  const [brand, setBrand] = useState('')
  const [repShareable, setRepShareable] = useState(false)
  const [pending, setPending] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [confirmingRetire, setConfirmingRetire] = useState(false)
  const [retirePending, setRetirePending] = useState(false)
  const [retireError, setRetireError] = useState<string | null>(null)
  const editTriggerRef = useRef<HTMLButtonElement | null>(null)
  const replaceTriggerRef = useRef<HTMLButtonElement | null>(null)
  const retireTriggerRef = useRef<HTMLButtonElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  const beginEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!document) return
    editTriggerRef.current = e.currentTarget
    setTitle(document.title)
    setBrand(document.brand ?? '')
    setRepShareable(document.rep_shareable)
    setSaveError(null)
    setEditing(true)
  }

  const restoreFocus = () => {
    const trigger = editTriggerRef.current
    editTriggerRef.current = null
    if (trigger && window.document.contains(trigger)) {
      trigger.focus()
    } else {
      headingRef.current?.focus()
    }
  }

  const cancelEdit = () => {
    setEditing(false)
    setSaveError(null)
    restoreFocus()
  }

  const save = async () => {
    if (pending) return
    if (!title.trim()) {
      setSaveError('Title cannot be empty')
      return
    }
    setPending(true)
    setSaveError(null)
    try {
      await updateMetadata({
        title: title.trim(),
        brand: brand.trim() || null,
        repShareable,
      })
      setEditing(false)
      restoreFocus()
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : 'Failed to save changes'
      )
    } finally {
      setPending(false)
    }
  }

  const openReplace = (e: React.MouseEvent<HTMLButtonElement>) => {
    replaceTriggerRef.current = e.currentTarget
    setReplacing(true)
  }

  const closeReplace = () => {
    setReplacing(false)
    const trigger = replaceTriggerRef.current
    replaceTriggerRef.current = null
    if (trigger && window.document.contains(trigger)) {
      trigger.focus()
    } else {
      headingRef.current?.focus()
    }
  }

  const openRetire = (e: React.MouseEvent<HTMLButtonElement>) => {
    retireTriggerRef.current = e.currentTarget
    setRetireError(null)
    setConfirmingRetire(true)
  }

  const closeRetire = () => {
    setConfirmingRetire(false)
    setRetireError(null)
    // On cancel the trigger is still mounted; return focus to it. After a
    // successful retire the trigger has unmounted (status flipped, control
    // gone), so fall back to the heading rather than a detached node.
    const trigger = retireTriggerRef.current
    retireTriggerRef.current = null
    if (trigger && window.document.contains(trigger)) {
      trigger.focus()
    } else {
      headingRef.current?.focus()
    }
  }

  const confirmRetire = async () => {
    if (retirePending) return
    setRetirePending(true)
    setRetireError(null)
    try {
      await retire()
      // Success: the document is now retired and re-rendered read-only. The
      // retire trigger unmounts, so send focus to the heading.
      retireTriggerRef.current = null
      setConfirmingRetire(false)
      headingRef.current?.focus()
    } catch (err) {
      // Stable, retryable copy — a trigger rejection (already retired) or a
      // stale-row message; the overlay stays open so the user can retry.
      setRetireError(
        err instanceof Error ? err.message : 'Failed to retire the document'
      )
    } finally {
      setRetirePending(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <BackLink />
        <div className="card p-6">
          <div className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-6 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error || !document) {
    return (
      <div className="space-y-6">
        <BackLink />
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error ??
            'This document no longer exists — it may have been removed.'}
        </div>
      </div>
    )
  }

  const content = latestVersion?.content_text ?? ''
  const isLong = content.length > PREVIEW_COLLAPSED_CHARS
  const shownContent =
    isLong && !expanded ? content.slice(0, PREVIEW_COLLAPSED_CHARS) : content

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="card p-6">
        <div className="flex items-start justify-between">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-bold text-gray-900 focus:outline-none"
          >
            {document.title}
          </h1>
          {/* Retired = filled gray badge; Active = quiet meta text (decision 9). */}
          {document.status === 'retired' ? (
            <span className="badge badge-gray">Retired</span>
          ) : (
            <span className="text-sm text-gray-500">Active</span>
          )}
        </div>

        {/* Provenance meta line — read-only; source filename is immutable. */}
        <p className="text-sm text-gray-500 mt-2">
          {document.source}
          {' · Added '}
          {document.created_at ? formatDateTime(document.created_at) : '—'}
          {' · Updated '}
          {document.updated_at ? formatDateTime(document.updated_at) : '—'}
          {document.created_by ? ' · Uploaded by a team member' : ''}
        </p>

        {saveError && (
          <div
            role="alert"
            className="mt-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
          >
            {saveError}
          </div>
        )}

        {editing ? (
          <div className="mt-5 space-y-4">
            <div className="form-group">
              <label htmlFor="edit-title" className="form-label">
                Title
              </label>
              <input
                id="edit-title"
                type="text"
                className="input"
                value={title}
                disabled={pending}
                autoFocus
                onChange={e => setTitle(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="edit-brand" className="form-label">
                Brand tag <span className="text-gray-400">(optional)</span>
              </label>
              <input
                id="edit-brand"
                type="text"
                className="input"
                value={brand}
                disabled={pending}
                placeholder="Leave blank for all brands"
                onChange={e => setBrand(e.target.value)}
              />
            </div>
            <div className="flex items-start gap-2">
              <input
                id="edit-rep-shareable"
                type="checkbox"
                checked={repShareable}
                disabled={pending}
                onChange={e => setRepShareable(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <label
                htmlFor="edit-rep-shareable"
                className="text-sm text-gray-700"
              >
                Rep-shareable
                <span className="block text-gray-500">
                  Reps can receive links to this document in recap emails.
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={cancelEdit} disabled={pending}>
                Cancel
              </Button>
              <Button onClick={save} loading={pending} disabled={!title.trim()}>
                Save changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-5">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Brand</dt>
                <dd className="text-gray-900 mt-0.5">
                  {document.brand ? (
                    document.brand
                  ) : (
                    <span className="text-gray-400">All brands</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Sharing</dt>
                <dd className="text-gray-900 mt-0.5">
                  {document.rep_shareable ? 'Shareable' : 'Internal'}
                </dd>
              </div>
            </dl>
            {document.status === 'active' ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={beginEdit}>
                  <Pencil className="w-3.5 h-3.5 mr-1" />
                  Edit metadata
                </Button>
                {/* Destructive-but-safe: outline, never red; the verb is explicit
                    and the action is confirm-gated + terminal (EXPERIENCE.md). */}
                <Button variant="outline" size="sm" onClick={openRetire}>
                  <Archive className="w-3.5 h-3.5 mr-1" />
                  Retire document
                </Button>
              </div>
            ) : (
              // Retired = terminal: no edit/replace/retire controls, and copy
              // that says why (AC 4).
              <p className="mt-4 text-sm text-gray-500">
                This document is retired — upload a corrected document as a new
                document.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Read-only content preview (latest version) + Replace action. */}
      <div className="card p-6">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Content</h2>
          {document.status === 'active' && (
            <Button variant="outline" size="sm" onClick={openReplace}>
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Replace content
            </Button>
          )}
        </div>
        {content ? (
          <>
            <pre className="whitespace-pre-wrap break-words text-sm text-gray-700 font-sans">
              {shownContent}
              {isLong && !expanded ? '…' : ''}
            </pre>
            {isLong && (
              <button
                type="button"
                onClick={() => setExpanded(v => !v)}
                className="mt-3 inline-flex items-center text-sm text-brand-700 hover:text-brand-800"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Show full content
                  </>
                )}
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">
            No content is available for this document.
          </p>
        )}
      </div>

      {/* Version history — newest-first, each version deep-linkable + read-only.
          "Nothing disappears": no delete, no restore; a replacement is additive. */}
      <VersionHistory
        documentId={document.id}
        versions={versions}
        loading={loading}
      />

      {replacing && (
        <KbReplaceForm
          documentTitle={document.title}
          onReplace={replaceContent}
          onClose={closeReplace}
        />
      )}

      {/* Retire confirmation — consequence-naming, terminal. Inline fixed overlay
          (no Modal primitive / toast). Esc closes; focus returns to the trigger,
          or the heading once the trigger unmounts after a successful retire. */}
      {confirmingRetire && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            if (e.key === 'Escape') closeRetire()
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="retire-doc-title"
            aria-describedby={retireError ? 'retire-doc-error' : undefined}
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <h3
              id="retire-doc-title"
              className="text-lg font-semibold text-gray-900 mb-1"
            >
              Retire “{document.title}”
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Yak stops answering from it; versions remain readable.
            </p>
            {retireError && (
              <div
                id="retire-doc-error"
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {retireError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                autoFocus
                variant="ghost"
                onClick={closeRetire}
                disabled={retirePending}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={confirmRetire}
                loading={retirePending}
              >
                Retire document
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function VersionHistory({
  documentId,
  versions,
  loading,
}: {
  documentId: string
  versions: YakKbDocVersion[]
  loading: boolean
}) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Version history</h2>
      </div>
      {loading ? (
        <div className="space-y-2" aria-hidden="true">
          {[0, 1].map(i => (
            <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : versions.length === 0 ? (
        <p className="text-sm text-gray-500">No versions yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {versions.map((v, i) => (
            <li
              key={v.id}
              className="flex items-center justify-between py-2.5 text-sm"
            >
              <div className="min-w-0">
                <Link
                  to={`/kb/${documentId}/versions/${v.id}`}
                  className="text-brand-700 hover:text-brand-800 hover:underline focus:outline-none focus:underline font-medium"
                >
                  Version {v.version_number}
                </Link>
                {/* i === 0 because the list is newest-first. */}
                {i === 0 && (
                  <span className="ml-2 badge badge-gray">Current</span>
                )}
                <span className="block text-gray-500 mt-0.5 truncate">
                  {v.created_at ? formatDateTime(v.created_at) : '—'}
                  {v.original_filename ? ` · ${v.original_filename}` : ''}
                </span>
              </div>
              <Link
                to={`/kb/${documentId}/versions/${v.id}`}
                className="shrink-0 text-gray-500 hover:text-gray-700"
              >
                View
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function BackLink() {
  return (
    <Link
      to="/kb"
      className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
    >
      <ArrowLeft className="w-4 h-4 mr-1" />
      Knowledge Base
    </Link>
  )
}
